import { useState, useEffect } from 'react';
import { Loader2, Building2, Users, FolderKanban, Layers, CreditCard, TrendingUp, UserPlus } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PlatformDashboard = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await fetch(`${API_URL}/api/platform/dashboard/stats`, { credentials: 'include' });
        if (res.ok) setStats(await res.json());
      } catch (err) { console.error('Dashboard stats load failed:', err); } finally { setLoading(false); }
    };
    load();
  }, []); // API_URL is module constant, load once on mount

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="w-5 h-5 animate-spin text-slate-500" /></div>;
  if (!stats) return <p className="text-slate-500 text-sm">Failed to load stats</p>;

  const metrics = [
    { label: 'Total Companies', value: stats.total_companies, icon: Building2, color: 'text-blue-400 bg-blue-500/10' },
    { label: 'Active', value: stats.active_companies, icon: Building2, color: 'text-emerald-400 bg-emerald-500/10' },
    { label: 'Inactive', value: stats.inactive_companies, icon: Building2, color: 'text-red-400 bg-red-500/10' },
    { label: 'Total Users', value: stats.total_users, icon: Users, color: 'text-violet-400 bg-violet-500/10' },
    { label: 'Total Projects', value: stats.total_projects, icon: FolderKanban, color: 'text-amber-400 bg-amber-500/10' },
    { label: 'Total Units', value: stats.total_units, icon: Layers, color: 'text-cyan-400 bg-cyan-500/10' },
    { label: 'Active Subs', value: stats.active_subscriptions, icon: CreditCard, color: 'text-emerald-400 bg-emerald-500/10' },
    { label: 'Recent Signups', value: stats.recent_signups, sub: '30 days', icon: UserPlus, color: 'text-pink-400 bg-pink-500/10' },
  ];

  return (
    <div className="space-y-6" data-testid="platform-dashboard">
      <div>
        <h1 className="text-lg font-semibold text-slate-100">Dashboard</h1>
        <p className="text-xs text-slate-500 mt-0.5">Platform-wide analytics</p>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {metrics.map(m => (
          <div key={m.label} className="bg-slate-900 border border-slate-800 rounded-lg p-4" data-testid={`stat-${m.label.toLowerCase().replace(/\s/g, '-')}`}>
            <div className="flex items-center gap-2.5">
              <div className={`w-8 h-8 rounded flex items-center justify-center ${m.color}`}>
                <m.icon className="w-4 h-4" />
              </div>
              <div>
                <p className="text-xl font-bold text-slate-100 leading-none">{m.value}</p>
                <p className="text-[10px] text-slate-500 mt-1">{m.label}{m.sub ? ` (${m.sub})` : ''}</p>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* MRR + Plan Distribution */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div className="bg-slate-900 border border-slate-800 rounded-lg p-5">
          <div className="flex items-center gap-2 mb-3">
            <TrendingUp className="w-4 h-4 text-emerald-400" />
            <span className="text-xs font-medium text-slate-300">Monthly Recurring Revenue</span>
          </div>
          <p className="text-3xl font-bold text-emerald-400" data-testid="mrr-value">
            {stats.mrr.toLocaleString('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 })}
          </p>
          <p className="text-[10px] text-slate-500 mt-1">From {stats.active_subscriptions} active subscriptions</p>
        </div>

        <div className="bg-slate-900 border border-slate-800 rounded-lg p-5">
          <p className="text-xs font-medium text-slate-300 mb-3">Plan Distribution</p>
          <div className="space-y-2">
            {Object.entries(stats.plan_breakdown || {}).map(([name, count]) => {
              const total = Object.values(stats.plan_breakdown).reduce((a, b) => a + b, 0) || 1;
              const pct = Math.round((count / total) * 100);
              return (
                <div key={name} className="flex items-center gap-3">
                  <span className="text-xs text-slate-400 w-20 truncate">{name}</span>
                  <div className="flex-1 h-2 bg-slate-800 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-emerald-500/60 rounded-full transition-all"
                      style={{ width: `${pct}%` }}
                    />
                  </div>
                  <span className="text-xs text-slate-400 w-12 text-right">{count} ({pct}%)</span>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Unit Status */}
      <div className="bg-slate-900 border border-slate-800 rounded-lg p-5">
        <p className="text-xs font-medium text-slate-300 mb-3">Unit Status Breakdown (Platform-wide)</p>
        <div className="flex gap-6">
          <div>
            <p className="text-lg font-bold text-emerald-400">{stats.units_available}</p>
            <p className="text-[10px] text-slate-500">Available</p>
          </div>
          <div>
            <p className="text-lg font-bold text-amber-400">{stats.units_soft_blocked}</p>
            <p className="text-[10px] text-slate-500">Soft Blocked</p>
          </div>
          <div>
            <p className="text-lg font-bold text-blue-400">{stats.units_booked}</p>
            <p className="text-[10px] text-slate-500">Booked</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PlatformDashboard;
